import { useEffect } from 'react';
import { Outlet, Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import { Box, Flex, HStack, Text } from '@chakra-ui/react';
import type { PortalRole } from '@denise-augusta/domain';
import { initializeLattice } from '@/lattice/runtime';
import { usePortalRole, canAccess } from '@/auth/roles';

/**
 * Portal features and the role each requires. Everything is admin-only for
 * now; client features will be flagged 'client' as they are released.
 */
const PORTAL_LINKS: Array<{ to: string; label: string; role: PortalRole }> = [
  { to: '/portal', label: 'Dashboard', role: 'admin' },
  { to: '/portal/inquiries', label: 'Inquiries', role: 'admin' },
  { to: '/portal/content', label: 'Content', role: 'admin' },
  { to: '/portal/media', label: 'Media', role: 'admin' },
  { to: '/portal/settings', label: 'Settings', role: 'admin' },
];

/**
 * Portal shell — guards the /portal sub-route behind Entra ID and filters
 * navigation by the user's portal role. Unauthenticated visitors are sent
 * to the login page once MSAL settles.
 */
export function PortalLayout() {
  const isAuthenticated = useIsAuthenticated();
  const { instance, inProgress, accounts } = useMsal();
  const role = usePortalRole();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!isAuthenticated && inProgress === InteractionStatus.None) {
      navigate('/portal/login', { replace: true });
    }
  }, [isAuthenticated, inProgress, navigate]);

  useEffect(() => {
    if (isAuthenticated) void initializeLattice();
  }, [isAuthenticated]);

  if (!isAuthenticated) return null;

  // Signed in but no portal role assigned — no access. Roles are granted
  // with infrastructure/scripts/grant-portal-admin.sh.
  if (role === null) {
    return (
      <Flex minH="100vh" align="center" justify="center" bg="brand.wash" direction="column" gap="1.6rem" px="1.8rem" textAlign="center">
        <Box>
          <Text fontFamily="heading" fontSize="clamp(2rem, 4vw, 2.8rem)" fontWeight="300" color="brand.ink" mb="0.6rem">
            Not{' '}
            <Box as="em" fontStyle="italic" color="brand.mid">
              authorized
            </Box>
          </Text>
          <Text fontFamily="heading" fontStyle="italic" fontSize="1rem" color="ink.soft" maxW="440px" lineHeight="1.7">
            Your account ({accounts[0]?.username}) is signed in but has not been granted portal
            access. Ask the site administrator to authorize you — then sign out and back in.
          </Text>
        </Box>
        <Box
          as="button"
          onClick={() => instance.logoutRedirect()}
          bg="brand.mid"
          color="paper.white"
          fontSize="0.72rem"
          fontWeight="500"
          letterSpacing="0.15em"
          textTransform="uppercase"
          px="2.2rem"
          py="0.85rem"
          border="none"
          cursor="pointer"
          borderRadius="24px"
          _hover={{ bg: 'brand.ink' }}
        >
          Sign Out
        </Box>
      </Flex>
    );
  }

  return (
    <Box minH="100vh" bg="paper.warm">
      <Flex
        as="header"
        h="60px"
        align="center"
        justify="space-between"
        px={{ base: '1.5rem', md: '2.5rem' }}
        bg="brand.ink"
        color="paper.white"
      >
        <HStack gap="2rem">
          <Text fontFamily="heading" fontSize="1.2rem">
            Denise{' '}
            <Box as="em" fontStyle="italic">
              Augusta
            </Box>{' '}
            <Box as="span" fontSize="0.7rem" letterSpacing="0.2em" textTransform="uppercase" opacity="0.7" ml="0.5rem">
              Portal
            </Box>
          </Text>
          <HStack gap="0.5rem" display={{ base: 'none', md: 'flex' }}>
            {PORTAL_LINKS.filter((link) => canAccess(role, link.role)).map((link) => (
              <Box
                key={link.to}
                asChild
                px="0.9rem"
                py="0.4rem"
                borderRadius="8px"
                fontSize="0.75rem"
                fontWeight="500"
                letterSpacing="0.1em"
                textTransform="uppercase"
                textDecoration="none"
                color="paper.white"
                bg={location.pathname === link.to ? 'rgba(255,255,255,0.16)' : 'transparent'}
                _hover={{ bg: 'rgba(255,255,255,0.1)' }}
              >
                <RouterLink to={link.to}>{link.label}</RouterLink>
              </Box>
            ))}
          </HStack>
        </HStack>
        <HStack gap="1rem">
          <Text fontSize="0.75rem" opacity="0.8" display={{ base: 'none', md: 'block' }}>
            {accounts[0]?.username}
            <Box as="span" ml="0.5rem" px="0.5rem" py="0.1rem" borderRadius="10px" bg="rgba(255,255,255,0.14)" fontSize="0.6rem" letterSpacing="0.12em" textTransform="uppercase">
              {role}
            </Box>
          </Text>
          <Box
            asChild
            fontSize="0.72rem"
            letterSpacing="0.1em"
            textTransform="uppercase"
            textDecoration="none"
            color="paper.white"
            opacity="0.8"
            _hover={{ opacity: 1 }}
          >
            <RouterLink to="/">View Site</RouterLink>
          </Box>
          <Box
            as="button"
            onClick={() => instance.logoutRedirect()}
            fontSize="0.72rem"
            letterSpacing="0.1em"
            textTransform="uppercase"
            bg="transparent"
            border="1px solid rgba(255,255,255,0.4)"
            color="paper.white"
            px="0.9rem"
            py="0.35rem"
            borderRadius="16px"
            cursor="pointer"
            _hover={{ bg: 'rgba(255,255,255,0.1)' }}
          >
            Sign Out
          </Box>
        </HStack>
      </Flex>

      <Box maxW="1100px" mx="auto" px={{ base: '1.5rem', md: '2.5rem' }} py="2.5rem">
        <Outlet />
      </Box>
    </Box>
  );
}
